import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSubmitContact, useSettings } from '../hooks/useCMS';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Send, 
  MessageCircle,
  ArrowLeft,
  CheckCircle,
  Globe,
  Video
} from 'lucide-react';
import { FadeIn, AnimatedCard } from '../components/ui/AnimatedCard';
import toast from 'react-hot-toast';

const contactSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  phone: z.string().optional(),
  subject: z.string().optional(),
  message: z.string().min(10, 'Message must be at least 10 characters'),
});

type ContactForm = z.infer<typeof contactSchema>;

export function Contact() {
  const { data: settings } = useSettings();
  const submitContact = useSubmitContact();
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactForm>({
    resolver: zodResolver(contactSchema),
  });

  const onSubmit = async (data: ContactForm) => {
    try {
      await submitContact.mutateAsync(data);
      toast.success('Message sent successfully!');
      setSubmitted(true);
      reset();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to send message');
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <FadeIn>
        <nav className="flex items-center gap-2 text-sm text-slate-500 mb-6">
          <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
          <span>/</span>
          <span className="text-slate-900 font-medium">Contact Us</span>
        </nav>

        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900">Contact Us</h1>
          <p className="text-slate-500 mt-2">We'd love to hear from you</p>
        </div>
      </FadeIn>

      <div className="grid md:grid-cols-2 gap-8">
        <FadeIn delay={0.1}>
          <div className="space-y-6">
            <AnimatedCard className="p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Get in Touch</h2>
              <div className="space-y-4">
                {settings?.contact_email && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-100 text-primary-600 rounded-xl flex items-center justify-center">
                      <Mail className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Email</p>
                      <a href={`mailto:${settings.contact_email}`} className="text-slate-900 font-medium hover:text-primary-600">
                        {settings.contact_email}
                      </a>
                    </div>
                  </div>
                )}
                
                {settings?.contact_phone && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-secondary-100 text-secondary-600 rounded-xl flex items-center justify-center">
                      <Phone className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Phone</p>
                      <a href={`tel:${settings.contact_phone}`} className="text-slate-900 font-medium hover:text-primary-600">
                        {settings.contact_phone}
                      </a>
                    </div>
                  </div>
                )}
                
                {settings?.contact_address && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-accent-100 text-accent-600 rounded-xl flex items-center justify-center">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Address</p>
                      <p className="text-slate-900 font-medium">{settings.contact_address}</p>
                    </div>
                  </div>
                )}
              </div>
            </AnimatedCard>

            <AnimatedCard className="p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Follow Us</h2>
              <div className="flex gap-3">
                {settings?.facebook_url && (
                  <a
                    href={settings.facebook_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center hover:bg-blue-200 transition-colors"
                  >
                    <Globe className="w-6 h-6" />
                  </a>
                )}
                {settings?.youtube_url && (
                  <a
                    href={settings.youtube_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-red-100 text-red-600 rounded-xl flex items-center justify-center hover:bg-red-200 transition-colors"
                  >
                    <Video className="w-6 h-6" />
                  </a>
                )}
                {settings?.telegram_url && (
                  <a
                    href={settings.telegram_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-sky-100 text-sky-600 rounded-xl flex items-center justify-center hover:bg-sky-200 transition-colors"
                  >
                    <MessageCircle className="w-6 h-6" />
                  </a>
                )}
              </div>
            </AnimatedCard>
          </div>
        </FadeIn>

        <FadeIn delay={0.2}>
          <AnimatedCard className="p-6">
            {submitted ? (
              <div className="text-center py-8">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="w-16 h-16 bg-secondary-100 text-secondary-600 rounded-full flex items-center justify-center mx-auto mb-4"
                >
                  <CheckCircle className="w-8 h-8" />
                </motion.div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Message Sent!</h3>
                <p className="text-slate-500 mb-4">We'll get back to you soon.</p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="text-primary-600 font-medium hover:text-primary-700"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <>
                <h2 className="text-xl font-bold text-slate-900 mb-4">Send Message</h2>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                    <input
                      {...register('name')}
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                      placeholder="Your name"
                    />
                    {errors.name && (
                      <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                    <input
                      {...register('email')}
                      type="email"
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                      placeholder="your@email.com"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                    <input
                      {...register('phone')}
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                      placeholder="01XXXXXXXXX"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Subject</label>
                    <input
                      {...register('subject')}
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                      placeholder="Message subject"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Message *</label>
                    <textarea
                      {...register('message')}
                      rows={4}
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all resize-none"
                      placeholder="Your message..."
                    />
                    {errors.message && (
                      <p className="text-red-500 text-sm mt-1">{errors.message.message}</p>
                    )}
                  </div>

                  <motion.button
                    type="submit"
                    disabled={submitContact.isPending}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-primary-600 to-secondary-500 text-white font-bold rounded-xl hover:shadow-lg transition-shadow disabled:opacity-50"
                  >
                    <Send className="w-4 h-4" />
                    {submitContact.isPending ? 'Sending...' : 'Send Message'}
                  </motion.button>
                </form>
              </>
            )}
          </AnimatedCard>
        </FadeIn>
      </div>
    </div>
  );
}
